import type {
  OrganizationManager,
  VolunteerOrgActivityDomain,
  VolunteerOrgProject,
  VolunteerOrganization,
} from "@entities";
import { organizationManagerRepo, volunteerOrganizationRepo } from "@repositories";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { CommunicationRecordManager } from "../../../components/CommunicationRecordManager/CommunicationRecordManager";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
  VolunteerOrgContactsFields,
  VolunteerOrgDomainsPicker,
  VolunteerOrgEventFlagsFields,
  VolunteerOrgHandlingManagerField,
  VolunteerOrgLocationField,
} from "./VolunteerOrgSharedFields";
import { VolunteerOrgAssignmentsPanel } from "./VolunteerOrgAssignmentsPanel";
import { activityDomainIds, syncVolunteerOrgActivities } from "./sync-activities";
import { syncVolunteerOrgContacts } from "./sync-contacts";
import {
  canonicalVolunteerOrgContactColumns,
  normalizeVolunteerOrgContactDrafts,
  defaultVolunteerOrgContactDrafts,
  isVolunteerOrgBranch,
  volunteerOrgContactDrafts,
  type VolunteerOrgContactDraft,
} from "@shared/helpers/volunteer-organization";
import {
  ChoiceTile,
  dialogTabSx,
  FormSection,
  primaryActionSx,
  quietButtonSx,
  surfaceCardSx,
} from "./volunteer-org-form-ui";

type Structure = "independent" | "branch";

type Draft = {
  contacts: VolunteerOrgContactDraft[];
  name: string;
  description: string;
  relationshipType: string;
  comments: string;
  handlingManagerId: string;
  address: string;
  coordinates: { lat: number; lng: number } | null;
  volunteerCapacity: string;
  interestedInRoutine: boolean;
  interestedInOutbreak: boolean;
  interestedInEmergency: boolean;
  domainIds: string[];
};

const emptyDraft = (): Draft => ({
  name: "",
  description: "",
  relationshipType: "",
  comments: "",
  handlingManagerId: "",
  address: "",
  coordinates: null,
  volunteerCapacity: "",
  interestedInRoutine: false,
  interestedInOutbreak: false,
  interestedInEmergency: false,
  domainIds: [],
  contacts: defaultVolunteerOrgContactDrafts(),
});

export function VolunteerOrgParentDialog({
  open,
  organization,
  domains,
  projects,
  createDefaults,
  initialTab = 0,
  onClose,
  onSaved,
  onAddBranch,
  onOpenBranch,
}: {
  open: boolean;
  organization: VolunteerOrganization | null;
  domains: VolunteerOrgActivityDomain[];
  projects: VolunteerOrgProject[];
  createDefaults?: {
    name?: string;
    comments?: string;
    handlingManagerId?: string;
    volunteeringCoordinatorPhone?: string;
  };
  initialTab?: number;
  onClose: () => void;
  onSaved: (org: VolunteerOrganization, created: boolean) => void;
  onAddBranch: (parent: VolunteerOrganization) => void;
  onOpenBranch?: (
    parent: VolunteerOrganization,
    branch: VolunteerOrganization,
  ) => void;
}) {
  const [tab, setTab] = useState(0);
  const [pendingActivation, setPendingActivation] = useState(false);
  const pendingActivationRef = useRef(false);
  const loadedOrgId = useRef<string | null | undefined>(undefined);
  const createdOrgId = useRef<string | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [handlingManager, setHandlingManager] =
    useState<OrganizationManager | null>(null);
  const [structure, setStructure] = useState<Structure>("independent");
  const [linkedParent, setLinkedParent] = useState<VolunteerOrganization | null>(
    null,
  );
  const parentId = organization?.id;

  const { data: branches = [], isLoading: branchesLoading } = useQuery({
    queryKey: ["volunteer-org-parent-branches", parentId],
    enabled: open && Boolean(parentId),
    queryFn: () =>
      volunteerOrganizationRepo.find({
        where: { parentId: parentId! },
        orderBy: { name: "asc" },
        limit: 500,
      }),
  });

  const { data: parentOptions = [] } = useQuery({
    queryKey: ["volunteer-org-parent-options", organization?.id],
    enabled: open,
    queryFn: () =>
      volunteerOrganizationRepo.find({
        where: organization?.id
          ? { parentId: null, id: { $ne: organization.id } }
          : { parentId: null },
        orderBy: { name: "asc" },
        limit: 500,
      }),
  });

  const hasChildren =
    branches.length > 0 || (organization?.branches?.length ?? 0) > 0;
  const resolvingChildren =
    Boolean(organization) && branchesLoading && !hasChildren;
  const orgMode = hasChildren
    ? "parent"
    : resolvingChildren
      ? "loading"
      : structure === "branch"
        ? "branch"
        : "independent";
  const showBranchesTab = Boolean(organization) && orgMode !== "branch";
  const showAssignmentsTab =
    Boolean(organization) && (orgMode === "independent" || orgMode === "branch");
  const showCommunicationTab = Boolean(organization);
  let nextTab = 1;
  const branchesTab = showBranchesTab ? nextTab++ : -1;
  const assignmentsTab = showAssignmentsTab ? nextTab++ : -1;
  const communicationTab = showCommunicationTab ? nextTab++ : -1;

  useEffect(() => {
    if (!open) {
      pendingActivationRef.current = false;
      loadedOrgId.current = undefined;
      createdOrgId.current = null;
      setPendingActivation(false);
      return;
    }
    const nextId = organization?.id ?? null;
    if (loadedOrgId.current === nextId) return;
    loadedOrgId.current = nextId;
    if (!pendingActivationRef.current) setTab(initialTab);
    setError(null);
    const defaultManagerId = createDefaults?.handlingManagerId ?? "";
    if (!organization) {
      setStructure("independent");
      setLinkedParent(null);
      setDraft({
        ...emptyDraft(),
        name: createDefaults?.name ?? "",
        comments: createDefaults?.comments ?? "",
        handlingManagerId: defaultManagerId,
        contacts: volunteerOrgContactDrafts({
          volunteeringCoordinatorPhone:
            createDefaults?.volunteeringCoordinatorPhone ?? "",
        }),
      });
      if (defaultManagerId) {
        void organizationManagerRepo
          .findId(defaultManagerId, { include: { organization: true } })
          .then((manager) => setHandlingManager(manager ?? null));
      } else {
        setHandlingManager(null);
      }
      return;
    }
    const isBranch = isVolunteerOrgBranch(organization);
    setStructure(isBranch ? "branch" : "independent");
    setLinkedParent(organization.parent ?? null);
    setDraft({
      name: organization.name ?? "",
      description: organization.description ?? "",
      relationshipType: organization.relationshipType ?? "",
      comments: organization.comments ?? "",
      handlingManagerId: organization.handlingManagerId ?? "",
      address: organization.address ?? "",
      coordinates: organization.coordinates ?? null,
      volunteerCapacity:
        organization.volunteerCapacity == null
          ? ""
          : String(organization.volunteerCapacity),
      interestedInRoutine: organization.interestedInRoutine,
      interestedInOutbreak: organization.interestedInOutbreak,
      interestedInEmergency: organization.interestedInEmergency,
      domainIds: activityDomainIds(organization.activities),
      contacts: volunteerOrgContactDrafts(organization),
    });
    setHandlingManager(organization.handlingManager ?? null);
    if (!organization.handlingManager && organization.handlingManagerId) {
      void organizationManagerRepo
        .findId(organization.handlingManagerId, {
          include: { organization: true },
        })
        .then((manager) => setHandlingManager(manager ?? null));
    }
  }, [
    open,
    organization,
    createDefaults?.name,
    createDefaults?.comments,
    createDefaults?.handlingManagerId,
    createDefaults?.volunteeringCoordinatorPhone,
    initialTab,
  ]);

  useEffect(() => {
    if (!open || !pendingActivation || !organization || resolvingChildren) return;
    if (assignmentsTab < 0) return;
    setTab(assignmentsTab);
    pendingActivationRef.current = false;
    setPendingActivation(false);
  }, [open, pendingActivation, organization, resolvingChildren, assignmentsTab]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      if (orgMode === "loading") return;
      if (orgMode === "branch" && !linkedParent?.id) {
        throw new Error("בחרו ארגון אב לשלוחה");
      }
      const contacts = normalizeVolunteerOrgContactDrafts(draft.contacts);
      const volunteerCapacity = (() => {
        const raw = draft.volunteerCapacity.trim();
        if (!raw) return null;
        const value = Number(raw);
        if (!Number.isFinite(value) || value < 0) {
          throw new Error("קיבולת מתנדבים לא תקינה");
        }
        return Math.floor(value);
      })();
      const payload = {
        name: draft.name,
        description: draft.description,
        relationshipType: draft.relationshipType,
        comments: draft.comments,
        handlingManagerId: draft.handlingManagerId || null,
        parentId: orgMode === "branch" ? linkedParent!.id : null,
        ...(orgMode === "parent"
          ? {}
          : {
              address: draft.address,
              coordinates: draft.coordinates,
              volunteerCapacity,
              interestedInRoutine: draft.interestedInRoutine,
              interestedInOutbreak: draft.interestedInOutbreak,
              interestedInEmergency: draft.interestedInEmergency,
            }),
        ...canonicalVolunteerOrgContactColumns(contacts),
      };
      const created = !organization;
      const retryId = created ? createdOrgId.current : null;
      const saved = organization
        ? await volunteerOrganizationRepo.update(organization.id, payload)
        : retryId
          ? await volunteerOrganizationRepo.update(retryId, payload)
          : await volunteerOrganizationRepo.insert(payload);
      if (created) createdOrgId.current = saved.id;
      await syncVolunteerOrgActivities(saved.id, draft.domainIds);
      await syncVolunteerOrgContacts(saved.id, contacts);
      if (created && orgMode !== "parent") {
        pendingActivationRef.current = true;
        setPendingActivation(true);
      }
      onSaved(saved, created);
    } catch (err) {
      setError(err instanceof Error ? err.message : "שמירה נכשלה");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth dir="rtl">
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          py: 1.5,
        }}
      >
        <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
          {organization ? organization.name : "ארגון חדש"}
        </Typography>
        <IconButton onClick={onClose} aria-label="סגירה" size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Tabs value={tab} onChange={(_, next) => setTab(next)} sx={dialogTabSx}>
          <Tab label="פרטים" />
          {showBranchesTab ? (
            <Tab label={`שלוחות${branches.length ? ` (${branches.length})` : ""}`} />
          ) : null}
          {showAssignmentsTab ? <Tab label="שיבוצים" /> : null}
          {showCommunicationTab ? <Tab label="תקשורת" /> : null}
        </Tabs>
        {tab === 0 ? (
          <Stack gap={2}>
            <FormSection
              title="סוג הארגון"
              description={
                orgMode === "parent"
                  ? "ארגון אב הוא מטה. ההפעלה נעשית דרך השלוחות."
                  : "ארגון יכול לעמוד בפני עצמו, או להיות שלוחה של ארגון קיים."
              }
            >
              {orgMode === "loading" ? (
                <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                  טוען סוג ארגון...
                </Typography>
              ) : orgMode === "parent" ? (
                <ChoiceTile
                  selected
                  label="ארגון אב"
                  onClick={() => undefined}
                />
              ) : (
                <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
                  <ChoiceTile
                    selected={orgMode === "independent"}
                    label="ארגון עצמאי"
                    onClick={() => setStructure("independent")}
                  />
                  <ChoiceTile
                    selected={orgMode === "branch"}
                    label="שלוחה של ארגון קיים"
                    onClick={() => setStructure("branch")}
                  />
                </Stack>
              )}
              {orgMode === "parent" ? (
                <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                  לארגון הזה יש שלוחות, לכן הוא נשאר ארגון אב.
                </Typography>
              ) : null}
              {orgMode === "branch" ? (
                <Autocomplete
                  options={
                    linkedParent &&
                    !parentOptions.some((row) => row.id === linkedParent.id)
                      ? [linkedParent, ...parentOptions]
                      : parentOptions
                  }
                  value={linkedParent}
                  onChange={(_, value) => setLinkedParent(value)}
                  getOptionLabel={(row) => row.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="ארגון אב"
                      required
                    />
                  )}
                />
              ) : null}
            </FormSection>
            <FormSection title="פרופיל">
              <Stack gap={1.5}>
                <TextField
                  size="small"
                  label="שם הארגון"
                  required
                  value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
                <TextField
                  size="small"
                  label="תיאור כללי"
                  multiline
                  minRows={2}
                  value={draft.description}
                  onChange={(event) =>
                    setDraft({ ...draft, description: event.target.value })
                  }
                />
                <TextField
                  size="small"
                  label="סוג הקשר"
                  value={draft.relationshipType}
                  onChange={(event) =>
                    setDraft({ ...draft, relationshipType: event.target.value })
                  }
                />
                <VolunteerOrgHandlingManagerField
                  value={handlingManager}
                  onChange={(manager) => {
                    setHandlingManager(manager);
                    setDraft({
                      ...draft,
                      handlingManagerId: manager?.id ?? "",
                    });
                  }}
                />
                <TextField
                  size="small"
                  label="הערות פנימיות"
                  multiline
                  minRows={2}
                  value={draft.comments}
                  onChange={(event) =>
                    setDraft({ ...draft, comments: event.target.value })
                  }
                />
              </Stack>
            </FormSection>
            {orgMode === "independent" || orgMode === "branch" ? (
              <>
                <FormSection
                  title="הפעלה"
                  description={
                    orgMode === "branch"
                      ? "כתובת וקיבולת של השלוחה. אחרי השמירה ממשיכים לשיבוץ."
                      : "כתובת, קיבולת וסוג אירוע. אחרי השמירה ממשיכים לשיבוץ לפרויקט ולמשימות."
                  }
                >
                  <Stack gap={1.5}>
                    <VolunteerOrgLocationField
                      value={{ address: draft.address, coordinates: draft.coordinates }}
                      onChange={(next) => setDraft({ ...draft, ...next })}
                      required={orgMode === "branch"}
                    />
                    <TextField
                      size="small"
                      label="קיבולת מתנדבים"
                      type="number"
                      value={draft.volunteerCapacity}
                      onChange={(event) =>
                        setDraft({ ...draft, volunteerCapacity: event.target.value })
                      }
                    />
                  </Stack>
                </FormSection>
                <VolunteerOrgEventFlagsFields
                  value={draft}
                  onChange={(next) => setDraft({ ...draft, ...next })}
                  description={
                    orgMode === "branch"
                      ? "אפשר לסמן כמה. בלי סימון נלקחים הסוגים של הארגון האב."
                      : "באילו מצבים אפשר להפעיל את הארגון. אפשר לסמן כמה."
                  }
                  emptyHint={
                    orgMode === "branch"
                      ? "בלי סימון נלקחים סוגי האירוע של הארגון האב."
                      : "בלי סימון הארגון לא יופיע בסינון לפי סוג אירוע."
                  }
                />
              </>
            ) : null}
            <VolunteerOrgDomainsPicker
              domains={domains}
              selectedIds={draft.domainIds}
              onChange={(domainIds) => setDraft({ ...draft, domainIds })}
              description={
                orgMode === "parent"
                  ? "תחומים של הארגון. שלוחות יורשות אותם ואפשר להוסיף להן תחומים."
                  : orgMode === "branch"
                    ? "נוסף על התחומים של הארגון האב."
                    : "תחומי העשייה של הארגון."
              }
            />
            <VolunteerOrgContactsFields
              value={draft.contacts}
              onChange={(contacts) => setDraft({ ...draft, contacts })}
            />
          </Stack>
        ) : null}
        {tab === branchesTab && showBranchesTab ? (
          <Stack gap={1.5}>
            {branchesLoading ? (
              <Typography color="text.secondary" sx={{ fontSize: 14 }}>
                טוען שלוחות...
              </Typography>
            ) : branches.length === 0 ? (
              <Alert
                severity="info"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      if (!organization) return;
                      onAddBranch(organization);
                    }}
                    sx={quietButtonSx}
                  >
                    הוספת שלוחה
                  </Button>
                }
              >
                אין שלוחות. אפשר להפעיל את הארגון עצמו, או להוסיף שלוחות אם יש כמה מוקדים.
              </Alert>
            ) : (
              branches.map((branch) => (
                <Stack
                  key={branch.id}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  sx={{ ...surfaceCardSx, cursor: "pointer" }}
                  onClick={() => {
                    if (!organization) return;
                    onOpenBranch?.(organization, branch);
                  }}
                >
                  <Stack sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700 }}>{branch.name}</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: 13 }} noWrap>
                      {branch.address || "אין כתובת"}
                      {branch.volunteerCapacity != null
                        ? ` · ${branch.volunteerCapacity} מתנדבים`
                        : ""}
                    </Typography>
                  </Stack>
                  <Button
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!organization) return;
                      onOpenBranch?.(organization, branch);
                    }}
                    sx={quietButtonSx}
                  >
                    עריכה
                  </Button>
                </Stack>
              ))
            )}
          </Stack>
        ) : null}
        {tab === assignmentsTab && showAssignmentsTab && organization ? (
          <VolunteerOrgAssignmentsPanel
            organization={organization}
            projects={projects}
          />
        ) : null}
        {tab === communicationTab && organization ? (
          <CommunicationRecordManager
            volunteerOrganizationId={organization.id}
            type="volunteer-organization"
            showAllRecords
            embedded
          />
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {showBranchesTab && organization ? (
          <Button
            onClick={() => onAddBranch(organization)}
            sx={{ ...quietButtonSx, marginInlineEnd: "auto" }}
          >
            הוסף שלוחה
          </Button>
        ) : null}
        <Button onClick={onClose} sx={quietButtonSx}>
          ביטול
        </Button>
        <Button
          variant="contained"
          disabled={
            saving ||
            resolvingChildren ||
            !draft.name.trim() ||
            (orgMode === "branch" && (!linkedParent || !draft.address.trim()))
          }
          onClick={() => void save()}
          sx={primaryActionSx}
        >
          {saving
            ? "שומר..."
            : !organization && orgMode !== "parent"
              ? "שמירה והמשך לשיבוץ"
              : "שמירה"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
